import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { RotateCcw, Sparkles, ExternalLink, Globe, Share2 } from "lucide-react";
import type { PageForEdit, SeoConfig, SeoFieldValue } from "../pages/types";
import { REGENERATE_SEO, UPDATE_SEO_CONFIG } from "../pages/mutations";
import { ImageUploadField } from "./inspector/ImageUploadField";
import { useToast } from "../../../components/ui/Toast";

// ── Constants ──────────────────────────────────────────────────────

const LIMITS = {
  metaTitle: 70,
  metaDescription: 160,
  ogTitle: 70,
  ogDescription: 200,
} as const;

// ── Helpers ────────────────────────────────────────────────────────

function parseSeoConfig(raw: string | null): SeoConfig {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as SeoConfig;
  } catch {
    return {};
  }
}

function fieldValue(field: SeoFieldValue | undefined): string {
  if (!field) return "";
  if (typeof field.value === "string") return field.value;
  return "";
}

function fieldMode(field: SeoFieldValue | undefined): "auto" | "custom" {
  return field?.mode ?? "auto";
}

function charCountClass(len: number, limit: number): string {
  if (len >= limit) return "text-rust";
  if (len >= limit * 0.9) return "text-amber-500";
  return "text-ink-5";
}

// ── Component ──────────────────────────────────────────────────────

interface SeoPanelProps {
  page: PageForEdit;
}

export function SeoPanel({ page }: SeoPanelProps) {
  const toast = useToast();
  const seo = useMemo(() => parseSeoConfig(page.seoConfig), [page.seoConfig]);

  // Local field state
  const [metaTitle, setMetaTitle] = useState(fieldValue(seo.metaTitle));
  const [metaDescription, setMetaDescription] = useState(
    fieldValue(seo.metaDescription),
  );
  const [ogTitle, setOgTitle] = useState(fieldValue(seo.ogTitle));
  const [ogDescription, setOgDescription] = useState(
    fieldValue(seo.ogDescription),
  );
  const [ogImageUrl, setOgImageUrl] = useState(fieldValue(seo.ogImageUrl));
  const [canonical, setCanonical] = useState(fieldValue(seo.canonical));
  const [noIndex, setNoIndex] = useState(seo.noIndex ?? false);

  // Track modes locally
  const [modes, setModes] = useState<Record<string, "auto" | "custom">>({
    metaTitle: fieldMode(seo.metaTitle),
    metaDescription: fieldMode(seo.metaDescription),
    ogTitle: fieldMode(seo.ogTitle),
    ogDescription: fieldMode(seo.ogDescription),
    ogImageUrl: fieldMode(seo.ogImageUrl),
    canonical: fieldMode(seo.canonical),
  });

  // Sync when page data changes (e.g. after regeneration)
  useEffect(() => {
    const fresh = parseSeoConfig(page.seoConfig);
    setMetaTitle(fieldValue(fresh.metaTitle));
    setMetaDescription(fieldValue(fresh.metaDescription));
    setOgTitle(fieldValue(fresh.ogTitle));
    setOgDescription(fieldValue(fresh.ogDescription));
    setOgImageUrl(fieldValue(fresh.ogImageUrl));
    setCanonical(fieldValue(fresh.canonical));
    setNoIndex(fresh.noIndex ?? false);
    setModes({
      metaTitle: fieldMode(fresh.metaTitle),
      metaDescription: fieldMode(fresh.metaDescription),
      ogTitle: fieldMode(fresh.ogTitle),
      ogDescription: fieldMode(fresh.ogDescription),
      ogImageUrl: fieldMode(fresh.ogImageUrl),
      canonical: fieldMode(fresh.canonical),
    });
  }, [page.seoConfig]);

  const [regenerateSeo, { loading: regenerating }] =
    useMutation(REGENERATE_SEO);
  const [updateSeoConfig, { loading: saving }] =
    useMutation(UPDATE_SEO_CONFIG);

  const handleFieldChange = useCallback(
    (field: string, value: string, setter: (v: string) => void) => {
      setter(value);
      setModes((prev) => ({ ...prev, [field]: "custom" }));
    },
    [],
  );

  const handleResetToAuto = useCallback(
    (field: string, setter: (v: string) => void) => {
      // Clear local value and set mode to auto
      setter("");
      setModes((prev) => ({ ...prev, [field]: "auto" }));
      // No immediate mutation -- persisted on Save
    },
    [],
  );

  const handleSave = useCallback(async () => {
    const config: Record<string, unknown> = {};
    const modeFields: [string, string, "auto" | "custom"][] = [
      ["metaTitle", metaTitle, modes.metaTitle],
      ["metaDescription", metaDescription, modes.metaDescription],
      ["ogTitle", ogTitle, modes.ogTitle],
      ["ogDescription", ogDescription, modes.ogDescription],
      ["ogImageUrl", ogImageUrl, modes.ogImageUrl],
      ["canonical", canonical, modes.canonical],
    ];
    for (const [field, value, mode] of modeFields) {
      if (mode === "custom" && value) {
        config[field] = { value, mode: "custom" };
      } else if (mode === "auto") {
        config[field] = { value: null, mode: "auto" };
      }
    }
    config.noIndex = noIndex;

    try {
      await updateSeoConfig({
        variables: { id: page.id, seoConfig: JSON.stringify(config) },
      });
      toast.push({ kind: "success", title: "SEO settings saved" });
    } catch (err) {
      toast.push({
        kind: "error",
        title: "Couldn't save SEO settings",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    }
  }, [
    page.id,
    metaTitle,
    metaDescription,
    ogTitle,
    ogDescription,
    ogImageUrl,
    canonical,
    noIndex,
    modes,
    updateSeoConfig,
    toast,
  ]);

  const handleRegenerate = useCallback(async () => {
    try {
      await regenerateSeo({ variables: { id: page.id } });
      toast.push({ kind: "success", title: "SEO fields regenerated" });
    } catch (err) {
      toast.push({
        kind: "error",
        title: "Couldn't regenerate SEO",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    }
  }, [page.id, regenerateSeo, toast]);

  // Preview values (use local edits or fallbacks)
  const previewTitle = metaTitle || page.title || "Untitled page";
  const previewDescription =
    metaDescription || page.description || "No description";
  const previewUrl = `/${page.slug}`;

  return (
    <div className="space-y-5">
      {/* Search preview */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <Globe size={13} className="text-ink-4" />
          <span className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-ink-4">
            Search preview
          </span>
        </div>
        <div className="rounded-[10px] border border-ink-6 bg-paper p-3">
          <div className="text-[13px] text-[#1a0dab] leading-tight truncate">
            {previewTitle}
          </div>
          <div className="text-[11px] text-[#006621] mt-0.5 truncate">
            {previewUrl}
          </div>
          <div className="text-[11.5px] text-ink-4 mt-1 line-clamp-2 leading-relaxed">
            {previewDescription}
          </div>
        </div>
      </div>

      {/* Social preview */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <Share2 size={13} className="text-ink-4" />
          <span className="text-[10.5px] font-bold tracking-[0.09em] uppercase text-ink-4">
            Social sharing preview
          </span>
        </div>
        <div className="rounded-[10px] border border-ink-6 bg-paper overflow-hidden">
          {(ogImageUrl || page.ogImageUrl) && (
            <div className="h-[120px] bg-paper-2 overflow-hidden">
              <img
                src={ogImageUrl || page.ogImageUrl || ""}
                alt="OG preview"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="p-3">
            <div className="text-[12px] text-ink-4 uppercase tracking-wider truncate">
              {page.slug}
            </div>
            <div className="text-[13px] font-semibold text-ink mt-0.5 truncate">
              {ogTitle || metaTitle || page.title || "Untitled"}
            </div>
            <div className="text-[11.5px] text-ink-4 mt-0.5 line-clamp-2 leading-relaxed">
              {ogDescription || metaDescription || page.description || ""}
            </div>
          </div>
        </div>
      </div>

      {/* Meta title */}
      <SeoField
        label="Meta title"
        value={metaTitle}
        limit={LIMITS.metaTitle}
        mode={modes.metaTitle}
        placeholder={page.title || "Page title"}
        onChange={(v) =>
          handleFieldChange("metaTitle", v.slice(0, LIMITS.metaTitle), setMetaTitle)
        }
        onReset={() => handleResetToAuto("metaTitle", setMetaTitle)}
      />

      {/* Meta description */}
      <SeoField
        label="Meta description"
        value={metaDescription}
        limit={LIMITS.metaDescription}
        mode={modes.metaDescription}
        placeholder={page.description || "Page description"}
        multiline
        onChange={(v) =>
          handleFieldChange(
            "metaDescription",
            v.slice(0, LIMITS.metaDescription),
            setMetaDescription,
          )
        }
        onReset={() => handleResetToAuto("metaDescription", setMetaDescription)}
      />

      {/* OG title */}
      <SeoField
        label="Social title"
        value={ogTitle}
        limit={LIMITS.ogTitle}
        mode={modes.ogTitle}
        placeholder={metaTitle || page.title || "Same as meta title"}
        onChange={(v) =>
          handleFieldChange("ogTitle", v.slice(0, LIMITS.ogTitle), setOgTitle)
        }
        onReset={() => handleResetToAuto("ogTitle", setOgTitle)}
      />

      {/* OG description */}
      <SeoField
        label="Social description"
        value={ogDescription}
        limit={LIMITS.ogDescription}
        mode={modes.ogDescription}
        placeholder={
          metaDescription || page.description || "Same as meta description"
        }
        multiline
        onChange={(v) =>
          handleFieldChange(
            "ogDescription",
            v.slice(0, LIMITS.ogDescription),
            setOgDescription,
          )
        }
        onReset={() => handleResetToAuto("ogDescription", setOgDescription)}
      />

      {/* Link preview image */}
      <div className="mb-[18px]">
        <div className="flex items-center justify-between mb-[7px]">
          <label className="text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4">
            Link preview image
          </label>
          <ModeBadge mode={modes.ogImageUrl} />
        </div>
        <ImageUploadField
          label=""
          value={ogImageUrl || undefined}
          onChange={(url) => {
            setOgImageUrl(url);
            setModes((prev) => ({ ...prev, ogImageUrl: "custom" }));
          }}
        />
        {modes.ogImageUrl === "custom" && (
          <button
            type="button"
            onClick={() => handleResetToAuto("ogImageUrl", setOgImageUrl)}
            className="mt-1 text-[10.5px] text-tide hover:text-tide-d transition-colors"
          >
            Reset to auto
          </button>
        )}
      </div>

      {/* Canonical URL */}
      <div className="mb-[18px]">
        <div className="flex items-center justify-between mb-[7px]">
          <label className="text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4">
            Canonical URL
          </label>
          <ModeBadge mode={modes.canonical} />
        </div>
        <div className="flex items-center gap-0">
          <span className="text-[12px] text-ink-5 font-mono px-2 py-2 bg-paper-2 border border-ink-6 border-r-0 rounded-l-[10px]">
            <ExternalLink size={12} />
          </span>
          <input
            type="url"
            value={canonical}
            onChange={(e) =>
              handleFieldChange("canonical", e.target.value, setCanonical)
            }
            placeholder={`Auto-derived from page URL`}
            className="flex-1 min-w-0 px-3 py-2 text-[13px] font-mono rounded-r-[10px] border border-ink-6 bg-paper text-ink placeholder:text-ink-5 focus:outline-none focus:border-tide focus:ring-1 focus:ring-tide transition-colors"
          />
        </div>
        {modes.canonical === "custom" && (
          <button
            type="button"
            onClick={() => handleResetToAuto("canonical", setCanonical)}
            className="mt-1 text-[10.5px] text-tide hover:text-tide-d transition-colors"
          >
            Reset to auto
          </button>
        )}
      </div>

      {/* noIndex toggle */}
      <div className="mb-[18px]">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={noIndex}
            onChange={(e) => setNoIndex(e.target.checked)}
            className="w-4 h-4 rounded border-ink-6 text-tide focus:ring-tide accent-tide"
          />
          <span className="text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4">
            Hide from search engines
          </span>
        </label>
        <p className="mt-1 text-[10.5px] text-ink-5 ml-[26px]">
          When enabled, search engines will be asked not to index this page.
        </p>
      </div>

      {/* Save */}
      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full text-[13px] font-semibold rounded-[10px] px-3.5 py-2.5 border border-tide bg-tide text-white hover:bg-tide-d disabled:opacity-45 disabled:cursor-default transition-colors"
      >
        {saving ? "Saving..." : "Save SEO settings"}
      </button>

      {/* Regenerate */}
      <button
        onClick={handleRegenerate}
        disabled={regenerating}
        className="flex items-center justify-center gap-2 w-full text-[13px] font-semibold rounded-[10px] px-3.5 py-2.5 border border-ink-6 text-ink hover:bg-paper-2 disabled:opacity-45 disabled:cursor-default transition-colors"
      >
        {regenerating ? (
          <RotateCcw size={14} className="animate-spin" />
        ) : (
          <Sparkles size={14} />
        )}
        {regenerating ? "Regenerating..." : "Regenerate SEO"}
      </button>
      <p className="text-[10.5px] text-ink-5 text-center -mt-2">
        Regenerates all auto-mode fields from page content using AI.
        Custom fields are not affected.
      </p>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────

function ModeBadge({ mode }: { mode: "auto" | "custom" }) {
  return (
    <span
      className={`text-[9.5px] font-bold tracking-[0.08em] uppercase px-1.5 py-0.5 rounded-full ${
        mode === "auto"
          ? "bg-tide/10 text-tide"
          : "bg-ink-6 text-ink-4"
      }`}
    >
      {mode}
    </span>
  );
}

interface SeoFieldProps {
  label: string;
  value: string;
  limit: number;
  mode: "auto" | "custom";
  placeholder: string;
  multiline?: boolean;
  onChange: (value: string) => void;
  onReset: () => void;
}

function SeoField({
  label,
  value,
  limit,
  mode,
  placeholder,
  multiline,
  onChange,
  onReset,
}: SeoFieldProps) {
  const fieldId = useId();

  return (
    <div className="mb-[18px]">
      <div className="flex items-center justify-between mb-[7px]">
        <label htmlFor={fieldId} className="text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4">
          {label}
        </label>
        <div className="flex items-center gap-2">
          <span className={`num text-[10.5px] ${charCountClass(value.length, limit)}`}>
            {value.length}/{limit}
          </span>
          <ModeBadge mode={mode} />
        </div>
      </div>
      {multiline ? (
        <textarea
          id={fieldId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full px-3 py-2 text-[13px] rounded-[10px] border border-ink-6 bg-paper text-ink placeholder:text-ink-5 focus:outline-none focus:border-tide focus:ring-1 focus:ring-tide transition-colors resize-none"
        />
      ) : (
        <input
          id={fieldId}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 text-[13px] rounded-[10px] border border-ink-6 bg-paper text-ink placeholder:text-ink-5 focus:outline-none focus:border-tide focus:ring-1 focus:ring-tide transition-colors"
        />
      )}
      {mode === "custom" && (
        <button
          type="button"
          onClick={onReset}
          className="mt-1 text-[10.5px] text-tide hover:text-tide-d transition-colors"
        >
          Reset to auto
        </button>
      )}
    </div>
  );
}
